import React from 'react';
import { Box, Paper, Typography, useTheme, IconButton, Tooltip } from '../ui';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { UseFormReturn } from 'react-hook-form';
import { VisibilityNode, evaluateVisibilityRule } from '../engine/runtime/visibility/dsl';
import QuickCheck from './QuickCheck';
import { getConfig } from '@config/env';
import { normalizeBudgetFrequency, toMonthlyBudgetAmount } from '@/shared/lib/budgetFrequency';
// Removed schema prompt system; warnings are now driven solely by the centralized engine
// Red Warning Engine (data-driven)
import type { OpenActionSpec, ResolvedWarning } from '@/shared/warnings/types';
import { useFieldWarnings } from '@/shared/warnings/useFieldWarnings';
import { useWarningsRefreshKey } from '@/shared/warnings/WarningsRefreshContext';
import FieldWarning from '@/components/warnings/FieldWarning';
// NI transformation for council tax → council rates
import { replaceCouncilTaxWithRates } from '@/shared/lib/niSchemaTransformer';

interface TooltipConfig {
  content: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

interface ClickableSegment {
  text: string;
  action: 'openHelpDialog' | 'openNotesDialog' | 'focusField' | 'openSchemaDialog';
  targetFieldName?: string;
  schemaKey?: string;
}

interface QuickCheckConfig {
  visibilityCondition: VisibilityNode;
  message: string;
  clickableSegments: ClickableSegment[];
}

interface QuestionGroupForDialogProps {
  label: string;
  helpText?: string;
  children: React.ReactNode;
  /**
   * When true, removes the outer card chrome (border, border colour, padding and
   * background) so the group's fields sit flat in the surrounding layout.
   * Defaults to false (boxed card) — preserves existing behavior for any other usage.
   */
  flat?: boolean;
  /**
   * Overrides the vertical gap (MUI spacing units) between the label and the
   * field below it. Defaults to undefined, which preserves the existing 24px
   * (mb: 3) header spacing. Opt-in only — existing usage is unaffected.
   */
  labelFieldGap?: number;
  mainTooltip?: TooltipConfig;
  showCalculatedIncome?: boolean;
  fieldName?: string;
  form?: UseFormReturn<any>;
  // Direct value props for use without react-hook-form
  amountValue?: number | string | null;
  frequencyValue?: string | null;
  onHelpClick?: () => void;
  onCalculatedIncomeHelpClick?: () => void;
  quickCheck?: QuickCheckConfig;
  onQuickCheckHelpOpen?: (() => void) | undefined;
  onQuickCheckNotesOpen?: (() => void) | undefined;
  onQuickCheckFieldFocus?: ((fieldName: string) => void) | undefined;
  onQuickCheckOpenSchema?: ((schemaKey: string) => void) | undefined;
  categoryKey?: string;
  onOpenFieldEdit?: ((schemaKey: string, fieldName: string, apiFieldName: string) => void) | undefined;
  onWarningAction?: ((action: OpenActionSpec, metadata?: { varId?: string }) => void) | undefined;
  /** When true, transforms "council tax" to "council rates" in Quick check warnings */
  isNorthernIreland?: boolean;
}

const QuestionGroupForDialog: React.FC<QuestionGroupForDialogProps> = ({ 
  label,
  helpText,
  children,
  flat = false,
  labelFieldGap,
  mainTooltip,
  showCalculatedIncome = false,
  fieldName,
  form,
  amountValue: directAmountValue,
  frequencyValue: directFrequencyValue,
  onHelpClick,
  onCalculatedIncomeHelpClick,
  quickCheck,
  onQuickCheckHelpOpen,
  onQuickCheckNotesOpen,
  onQuickCheckFieldFocus,
  onQuickCheckOpenSchema,
  categoryKey,
  onOpenFieldEdit,
  onWarningAction,
  isNorthernIreland = false,
}) => {
  const theme = useTheme();
  const warningsRefreshKey = useWarningsRefreshKey();

  // SpecialScreen can render duplicate inputs and react-hook-form can lag behind what
  // the user is currently typing. For warning calculations we prefer the ACTIVE DOM
  // values so cross-field prompts (e.g. HB vs Rent) update live while editing.
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [domOverrides, setDomOverrides] = React.useState<{
    amount: string | null;
    frequency: string | null;
  }>({ amount: null, frequency: null });
  const domOverridesRef = React.useRef(domOverrides);
  const [localWarningRefreshKey, setLocalWarningRefreshKey] = React.useState(0);

  React.useEffect(() => {
    domOverridesRef.current = domOverrides;
  }, [domOverrides]);

  React.useEffect(() => {
    if (!fieldName) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const pickBestInput = (
      root: ParentNode,
      selector: string,
      expectedName?: string
    ): HTMLInputElement | null => {
      const nodes = Array.from(root.querySelectorAll(selector)) as HTMLInputElement[];
      if (nodes.length === 0) return null;

      // Prefer the actively focused input inside this card
      const active = document.activeElement;
      if (
        active instanceof HTMLInputElement &&
        root instanceof HTMLElement &&
        root.contains(active) &&
        (!expectedName || active.name === expectedName)
      ) {
        return active;
      }

      // Prefer visible enabled inputs (SpecialScreen can render duplicates, including hidden/stale ones)
      const visible = nodes.filter((n) => {
        if (!n) return false;
        if (n.disabled) return false;
        if ((n as any).type === 'hidden') return false;
        const el = n as any;
        return el.offsetParent !== null;
      });
      if (visible.length > 0) return visible[visible.length - 1] ?? null;

      return nodes[nodes.length - 1] ?? null;
    };

    const readDom = (): { amount: string | null; frequency: string | null } => {
      const safeField = String(fieldName);
      const rootNode: ParentNode = rootRef.current ?? document;
      const rootEl = rootRef.current;

      // Amount can be nested or flat depending on how the schema renders the input.
      // Prefer the actively focused input inside this card, regardless of selector branch.
      // SpecialScreen can render duplicate inputs; the focused one is the source of truth while typing.
      const active = document.activeElement;
      let amountEl: HTMLInputElement | null = null;
      if (
        active instanceof HTMLInputElement &&
        rootEl instanceof HTMLElement &&
        rootEl.contains(active)
      ) {
        const activeName = String(active.getAttribute('name') || active.name || '');
        const looksLikeAmount =
          activeName === `${safeField}.amount` ||
          activeName === safeField ||
          activeName === 'amount' ||
          activeName.endsWith('.amount');
        if (looksLikeAmount) {
          amountEl = active;
        }
      }

      const fallbackAmountEl =
        pickBestInput(rootNode, `input[data-field-path="${safeField}.amount"]`, `${safeField}.amount`) ||
        pickBestInput(rootNode, `input[name="${safeField}.amount"]`, `${safeField}.amount`) ||
        pickBestInput(rootNode, `input[name="${safeField}"]`, safeField) ||
        pickBestInput(rootNode, `input[name="amount"]`, 'amount');
      if (!amountEl) {
        amountEl = fallbackAmountEl;
      }
      const amount = amountEl ? String(amountEl.value ?? '') : null;

      // Frequency is typically stored as a hidden input.
      const freqInputs = Array.from(
        rootNode.querySelectorAll(`[name="${safeField}.frequency"]`)
      ) as Array<HTMLInputElement | HTMLSelectElement>;
      let freqEl =
        freqInputs.length > 0 ? freqInputs[freqInputs.length - 1] : null;
      // Final fallback: flat "frequency" name inside the card.
      if (!freqEl) {
        const fallbackFreqInputs = Array.from(
          rootNode.querySelectorAll(`[name="frequency"]`)
        ) as Array<HTMLInputElement | HTMLSelectElement>;
        freqEl =
          fallbackFreqInputs.length > 0
            ? fallbackFreqInputs[fallbackFreqInputs.length - 1]
            : null;
      }
      const frequency = freqEl ? String((freqEl as any).value ?? '') : null;

      return { amount, frequency };
    };

    const updateFromDom = () => {
      const next = readDom();
      const prev = domOverridesRef.current;
      const changed =
        next.amount !== prev.amount || next.frequency !== prev.frequency;
      if (!changed) return;
      domOverridesRef.current = next;
      setDomOverrides(next);
      setLocalWarningRefreshKey((k) => k + 1);
    };

    // Initial read (ensures warnings are correct on first render)
    updateFromDom();

    const handler = (e: Event) => {
      const rootEl = rootRef.current;
      const targetNode = e.target as unknown;
      if (!rootEl || !(rootEl instanceof HTMLElement)) return;
      if (!(targetNode instanceof Node) || !rootEl.contains(targetNode)) return;

      const safeField = String(fieldName);

      // Prefer taking values directly from the event target (most reliable with duplicate inputs).
      const target = targetNode as any;
      const nameAttr =
        typeof target?.getAttribute === 'function'
          ? String(target.getAttribute('name') ?? '')
          : String(target?.name ?? '');
      const fieldPathAttr =
        typeof target?.getAttribute === 'function'
          ? String(target.getAttribute('data-field-path') ?? '')
          : '';

      const isAmountTarget =
        target instanceof HTMLInputElement &&
        (fieldPathAttr === `${safeField}.amount` ||
          nameAttr === `${safeField}.amount` ||
          nameAttr === safeField ||
          nameAttr === 'amount' ||
          nameAttr.endsWith('.amount'));

      const isFrequencyTarget =
        (target instanceof HTMLInputElement ||
          target instanceof HTMLSelectElement) &&
        (fieldPathAttr === `${safeField}.frequency` ||
          nameAttr === `${safeField}.frequency` ||
          nameAttr === 'frequency' ||
          nameAttr.endsWith('.frequency'));

      if (isAmountTarget || isFrequencyTarget) {
        const prev = domOverridesRef.current;
        const next = {
          amount: isAmountTarget ? String((target as HTMLInputElement).value ?? '') : prev.amount,
          frequency: isFrequencyTarget
            ? String((target as HTMLInputElement | HTMLSelectElement).value ?? '')
            : prev.frequency,
        };
        const changed = next.amount !== prev.amount || next.frequency !== prev.frequency;
        if (changed) {
          domOverridesRef.current = next;
          setDomOverrides(next);
          setLocalWarningRefreshKey((k) => k + 1);
        }
        return;
      }

      // Fallback: still attempt a DOM scan for other cases (e.g. frequency toggles).
      updateFromDom();
    };

    window.addEventListener('input', handler, true);
    window.addEventListener('change', handler, true);
    return () => {
      window.removeEventListener('input', handler, true);
      window.removeEventListener('change', handler, true);
    };
  }, [fieldName]);

  // Source of truth: prefer form values when a form is bound.
  // Direct props are intended for non-RHF/read-only scenarios.
  let amountValue: unknown = undefined;
  let frequencyValue: unknown = undefined;

  if (form && fieldName) {
    // Use watch so UI updates immediately on frequency chip clicks.
    amountValue = form.watch(`${fieldName}.amount`) ?? form.watch('amount');
    frequencyValue = form.watch(`${fieldName}.frequency`) ?? form.watch('frequency');
  } else {
    amountValue = directAmountValue;
    frequencyValue = directFrequencyValue;
  }

  // Evaluate Quick check visibility (gated by feature flag)
  const allFormValues = form?.watch() ?? {};
  const features = React.useMemo(() => getConfig().features || {}, []);
  const shouldShowQuickCheck = (features.useSchemaPrompts !== false) && quickCheck 
    ? evaluateVisibilityRule(quickCheck.visibilityCondition, allFormValues)
    : false;

  // Schema prompt system removed; centralized engine handles warnings

  // Parse helpText to add bold formatting
  const renderHelpText = (text: string) => {
    if (!text) return null;
    
    // Find text between "provide" and "income" to make it bold
    const boldPattern = /provide ([^.]+income[^.]*)/i;
    const match = text.match(boldPattern);
    
    if (match) {
      const parts = text.split(match[0]);
      return (
        <>
          {parts[0]}provide{' '}
          <Box data-testid="components-render-help-text-container-action-2" component="span" sx={{ fontWeight: 700 }}>
            {match[1]}
          </Box>
          {parts[1]}
        </>
      );
    }
    
    return text;
  };

  // Parse amount value (remove commas and convert to number)
  let parsedAmount: number | undefined = undefined;
  const amountForCalc =
    domOverrides.amount !== null ? domOverrides.amount : amountValue;
  if (typeof amountForCalc === 'string') {
    // Strip any currency symbols/spaces while allowing digits, decimal, minus.
    const cleaned = amountForCalc.replace(/[^0-9.-]+/g, '');
    const parsed = parseFloat(cleaned);
    parsedAmount = isNaN(parsed) ? undefined : parsed;
  } else if (typeof amountForCalc === 'number') {
    parsedAmount = amountForCalc;
  }

  // Frequency: prefer form state (updates reliably on chip click); fall back to DOM only if form value is blank.
  const rawFrequencyFromForm = frequencyValue;
  const rawFrequencyFromDom = domOverrides.frequency;
  const rawFrequencyForCalc =
    typeof rawFrequencyFromForm === 'string' && rawFrequencyFromForm.trim().length > 0
      ? rawFrequencyFromForm
      : typeof rawFrequencyFromDom === 'string' && rawFrequencyFromDom.trim().length > 0
        ? rawFrequencyFromDom
        : rawFrequencyFromForm;

  const normalizedFrequency = normalizeBudgetFrequency(rawFrequencyForCalc);
  const hasValidData =
    typeof parsedAmount === 'number' &&
    Number.isFinite(parsedAmount) &&
    normalizedFrequency !== undefined;
  const isMonthlyFrequency = normalizedFrequency === 'monthly';
  const monthlyIncome = hasValidData
    ? toMonthlyBudgetAmount(parsedAmount as number, normalizedFrequency)
    : 0;

  const effectiveAmountValue = amountForCalc;
  const effectiveFrequencyValue = rawFrequencyForCalc;

  const formValuesForWarnings = React.useMemo(() => {
    const base = { ...(allFormValues || {}) } as Record<string, any>;
    if (!fieldName) return base;

    const existing = base[fieldName];
    const group =
      existing && typeof existing === 'object' && !Array.isArray(existing)
        ? { ...(existing as Record<string, any>) }
        : {};

    if (effectiveAmountValue !== undefined) group.amount = effectiveAmountValue;
    if (effectiveFrequencyValue !== undefined)
      group.frequency = effectiveFrequencyValue;

    base[fieldName] = group;
    // Also set flattened dot-keys because the warning engine's getValueAtPath
    // prefers exact matches like "houseRent.amount" when present.
    // SpecialScreen/Edit dialog can leave stale dot-keys around, so overwrite them here.
    if (effectiveAmountValue !== undefined) {
      base[`${fieldName}.amount`] = effectiveAmountValue;
    }
    if (effectiveFrequencyValue !== undefined) {
      base[`${fieldName}.frequency`] = effectiveFrequencyValue;
    }
    return base;
  }, [allFormValues, fieldName, effectiveAmountValue, effectiveFrequencyValue]);

  const engineWarnings: ResolvedWarning[] = useFieldWarnings({
    fieldName: fieldName ?? undefined,
    categoryKey: categoryKey ?? undefined,
    formValues: formValuesForWarnings,
    max: 3, // Allow multiple warnings so limit-exceeded warnings aren't lost
    refreshKey: (warningsRefreshKey ?? 0) + localWarningRefreshKey,
  });

  return (
    <Paper data-testid="components-question-group-for-dialog-container-action-1"
      ref={rootRef}
      elevation={0}
      sx={{
        // `flat` strips the card chrome (border, border colour, padding, background)
        // so the group renders inline with the surrounding fields. The default keeps
        // the boxed card. `mb` (outer spacing) is preserved in both modes.
        p: flat ? 0 : 1.5,
        mb: 3,
        ...(flat
          ? { border: 'none', backgroundColor: 'transparent' }
          : {
              borderRadius: 1.5,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: 'white',
            }),
      }}
    >
      {/* Question Header */}
      <Box data-testid="components-question-group-for-dialog-container-action-2" sx={{ mb: labelFieldGap ?? 3 }}>
        <Box data-testid="components-question-group-for-dialog-container-action-3" 
          sx={{ 
            display: 'flex', 
            alignItems: 'flex-start',
            gap: 0.5,
            mb: 0.5,
          }}
        >
          <Typography data-testid="components-question-group-for-dialog-text-action-1"
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: '16px',
              lineHeight: '22px',
              flex: 1,
            }}
          >
            {label}
          </Typography>
          {(onHelpClick || mainTooltip) && (
            <Tooltip 
              title={onHelpClick ? "Help & FAQs" : (mainTooltip?.content || "")}
              placement={mainTooltip?.position || 'top'}
              arrow
            >
              <IconButton data-testid="components-question-group-for-dialog-btn-on-help-click"
                size="small"
                onClick={onHelpClick}
                sx={{
                  width: 24,
                  height: 24,
                  color: onHelpClick ? "#9CA3AF" : theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <HelpOutlineIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        
        {helpText && (
          <Typography data-testid="components-question-group-for-dialog-text-action-2"
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '14px',
              lineHeight: '116.68%',
              mt: 0.5,
            }}
          >
            {renderHelpText(helpText)}
          </Typography>
        )}
      </Box>

      {/* Children Content with Calculated Income Below (Not Overlapping) */}
      <Box data-testid="components-question-group-for-dialog-container-action-4">
        {React.Children.map(children, (child, index) => {
          // First child is the amount field - render it with calculated income below (not overlapping)
          if (index === 0) {
            const showCalc = showCalculatedIncome && hasValidData && monthlyIncome > 0 && !isMonthlyFrequency;
            return (
              <Box data-testid="components-question-group-for-dialog-container-action-5" key={`${fieldName || 'qg'}-amount-wrapper`} sx={{ mb: showCalc ? 0 : 1.5 }}>
                {/* Wrap child to control spacing */}
                <Box data-testid="components-question-group-for-dialog-container-action-6" sx={{ '& > *': { mb: '0 !important' } }}>
                  {child}
                </Box>
                {/* Calculated income displayed below (not overlapping) */}
                {showCalc && (
                  <Box data-testid="components-question-group-for-dialog-container-action-7"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1.5,
                      mt: 1,
                      backgroundColor: theme.palette.grey[50],
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography data-testid="components-question-group-for-dialog-text-calculated-monthly-amount:-£"
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '14px',
                      }}
                    >
                      Calculated monthly amount: £{monthlyIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                    {onCalculatedIncomeHelpClick && (
                      <Tooltip 
                        title="Help & FAQs"
                        placement="top"
                        arrow
                      >
                        <IconButton data-testid="components-question-group-for-dialog-btn-on-calculated-income-help-click"
                          size="small"
                          onClick={onCalculatedIncomeHelpClick}
                          sx={{
                            width: 18,
                            height: 18,
                            padding: 0,
                            color: "#9CA3AF",
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <HelpOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
            );
          }

          // Non-first children render as-is
          return <React.Fragment key={index}>{child}</React.Fragment>;
        })}
      </Box>

      {/* Quick check - Legacy support */}
      {shouldShowQuickCheck && quickCheck && (
        <QuickCheck
          message={isNorthernIreland ? replaceCouncilTaxWithRates(quickCheck.message) : quickCheck.message}
          segments={isNorthernIreland 
            ? quickCheck.clickableSegments.map(seg => ({
                ...seg,
                text: replaceCouncilTaxWithRates(seg.text),
              }))
            : quickCheck.clickableSegments}
          onHelpDialogOpen={onQuickCheckHelpOpen}
          onNotesDialogOpen={onQuickCheckNotesOpen}
          onFieldFocus={onQuickCheckFieldFocus}
          onOpenSchemaDialog={onQuickCheckOpenSchema}
        />
      )}

      {/* Schema prompt system removed */}

      {/* Engine-driven Warning (data-driven rules) */}
      {engineWarnings.map((w) => (
        <FieldWarning
          key={w.id}
          warning={w}
          onOpenFieldEdit={onOpenFieldEdit}
          onNotesDialogOpen={onQuickCheckNotesOpen}
          onWarningAction={onWarningAction}
          isNorthernIreland={isNorthernIreland}
        />
      ))}
    </Paper>
  );
};

export default QuestionGroupForDialog;

